import { computed, makeObservable, observable, runInAction } from "mobx"
import { formatUnits, parseUnits } from "viem"

import type { IGauge, IRevenueSource } from "@/domains/gauge"
import type { MinimalPoolApiData, MinimalPoolStoreDAO } from "@/domains/minimal-pool/types"
import type { TokenStore } from "@/domains/tokens/token-store"
import { LcAddress } from "@/domains/core"
import { dollarsPrettified } from "@/domains/library"
import { DefaultInfoPairValues } from "@/domains/on-chain/scalens/frontend-default-info-pairs"
import { ScalensUserInfoPairs } from "@/domains/on-chain/scalens/frontend-user-info-pairs"
import { ILiquidityPool } from "@/domains/pools"
import { ILiquidityPoolType } from "@/domains/pools/types"

import { ScalensUserManageMinimalPoolInfo } from "../on-chain/scalens/frontend-user-manage-minimal-pool"

export class MinimalPoolStore implements ILiquidityPool, IRevenueSource {
  address: LcAddress
  apiReserve0: bigint = 0n
  apiReserve1: bigint = 0n
  apiTotalSupply: bigint = 0n
  apiTvlUsd: number = 0
  apr: number = 0
  aprLastEpoch: number = 0
  decimals: number
  displayName: string
  displayType: string
  extendedType: string | null
  fee: number = 0
  gaugeAddress: LcAddress | undefined = undefined
  hasGauge: boolean = false
  gauge: IGauge | undefined = undefined
  id: number
  isStable: boolean
  onChainGaugeReserve0: bigint
  onChainGaugeReserve1: bigint
  onChainGaugeTotalSupply: bigint
  onChainReserve0: bigint
  onChainReserve1: bigint
  onChainTotalSupply: bigint
  onChainUserBalance: bigint
  onChainUserBalanceToken0: bigint
  onChainUserBalanceToken1: bigint
  onChainUserClaimableFees0: bigint
  onChainUserClaimableFees1: bigint
  onChainUserGaugeAllowance: bigint
  onChainUserGaugeBalance: bigint
  onChainUserGaugeGovernanceEarned: bigint
  onChainUserGaugeReserve0: bigint
  onChainUserGaugeReserve1: bigint
  onChainUserRouterAllowance: bigint
  onChainUserToken0RouterAllowance: bigint
  onChainUserToken1RouterAllowance: bigint
  tags: string[]
  token0: TokenStore
  token1: TokenStore

  constructor(data: MinimalPoolStoreDAO) {
    // Pool metadata
    this.id = data.id
    this.address = data.address
    this.displayName = data.displayName
    this.displayType = data.displayType
    this.extendedType = data.extendedType

    // Pool configuration
    this.decimals = data.decimals
    this.gaugeAddress = data.gaugeAddress
    this.hasGauge = data.hasGauge
    this.isStable = data.isStable

    // Financial data
    this.apr = data.apr
    this.aprLastEpoch = data.aprLastEpoch
    this.fee = data.fee
    // this.tvlUsd = data.tvlUsd

    this.apiReserve0 = data.reserve0
    this.apiReserve1 = data.reserve1
    this.apiTotalSupply = data.totalSupply
    this.apiTvlUsd = data.tvlUsd

    // Token information
    this.token0 = data.token0
    this.token1 = data.token1

    // Metadata
    this.tags = data.tags

    // Info pair variables (including user values)
    this.onChainGaugeReserve0 = 0n
    this.onChainGaugeReserve1 = 0n
    this.onChainGaugeTotalSupply = 0n
    this.onChainReserve0 = 0n
    this.onChainReserve1 = 0n
    this.onChainTotalSupply = 0n
    this.onChainUserBalance = 0n
    this.onChainUserBalanceToken0 = 0n
    this.onChainUserBalanceToken1 = 0n
    this.onChainUserClaimableFees0 = 0n
    this.onChainUserClaimableFees1 = 0n
    this.onChainUserGaugeAllowance = 0n
    this.onChainUserGaugeBalance = 0n
    this.onChainUserGaugeGovernanceEarned = 0n
    this.onChainUserGaugeReserve0 = 0n
    this.onChainUserGaugeReserve1 = 0n
    this.onChainUserRouterAllowance = 0n
    this.onChainUserToken0RouterAllowance = 0n
    this.onChainUserToken1RouterAllowance = 0n

    makeObservable(this, {
      apiTotalSupply: observable,
      apr: observable,
      aprLastEpoch: observable,
      fee: observable,
      onChainGaugeReserve0: observable,
      onChainGaugeReserve1: observable,
      onChainGaugeTotalSupply: observable,
      onChainReserve0: observable,
      onChainReserve1: observable,
      onChainTotalSupply: observable,
      onChainUserBalance: observable,
      onChainUserBalanceToken0: observable,
      onChainUserBalanceToken1: observable,
      onChainUserClaimableFees0: observable,
      onChainUserClaimableFees1: observable,
      onChainUserGaugeAllowance: observable,
      onChainUserGaugeBalance: observable,
      onChainUserGaugeGovernanceEarned: observable,
      onChainUserGaugeReserve0: observable,
      onChainUserGaugeReserve1: observable,
      onChainUserRouterAllowance: observable,
      onChainUserToken0RouterAllowance: observable,
      onChainUserToken1RouterAllowance: observable,
      tags: observable,
      token0: observable,
      token1: observable,
      gauge: observable,

      // Computed properties
      displayApr: computed,
      displayBalance: computed,
      displayFee: computed,
      displayIncomePerEpoch: computed,
      displayTvlUsd: computed,
      gaugeReserves: computed,
      gaugeTotalSupplyPercentage: computed,
      gaugeUserBalance: computed,
      gaugeUserBalancePercentage: computed,
      gaugeUserBalanceToken0: computed,
      gaugeUserBalanceToken1: computed,
      gaugeUserBalanceValueUSD: computed,
      gaugeUserValueUSD: computed,
      gaugeValueUSD: computed,
      imageURL: computed,
      isActive: computed,
      isBlueChip: computed,
      isNew: computed,
      bestPrice: computed,
      reserve0: computed,
      reserve1: computed,
      reserves: computed,
      scanTokenURL: computed,
      symbol: computed,
      tokens: computed,
      totalSupply: computed,
      tvlUsd: computed,
      type: computed,
      userBalance: computed,
      userBalancePercentage: computed,
      userBalanceToken0: computed,
      userBalanceToken1: computed,
      userBalanceValueUSD: computed,
      walletValueUSD: computed,
    })
  }

  get bestPrice(): number {
    return (
      Number(this.token0.formatUnits(this.reserve0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.reserve1)) * this.token1.bestPrice
    )
  }
  calculateValuePercentage(value: bigint, percentage: number): string {
    // Convert percentage to BigInt (multiply by 10000 to preserve 4 decimal places)
    const precision = 10000
    const percentageBigInt = BigInt(Math.floor(percentage * precision))

    // Calculate: (balance * percentage) / 10000
    const result = (value * percentageBigInt) / BigInt(precision)

    // format to this tokens decimals
    return this.formatUnits(result)
  }

  calculateValueUSD(amount: number): number {
    if (this.onChainTotalSupply === 0n) return 0
    const totalPoolValue = this.bestPrice // Total USD value of all reserves
    const totalSupplyFormatted = Number(this.formatUnits(this.onChainTotalSupply))
    const pricePerLpToken = totalPoolValue / totalSupplyFormatted
    return amount * pricePerLpToken
  }

  get displayApr(): string {
    return `${this.apr.toFixed(2)}%`
  }

  get displayBalance(): string {
    return this.formatUnits(this.onChainUserBalance)
  }

  get displayFee(): string {
    return `${(this.fee * 100).toFixed(4)}%`
  }

  get displayIncomePerEpoch(): string {
    return `$${(((this.gaugeValueUSD * this.apr) / 100 / 365) * 7).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}/epoch`
  }

  get displayTvlUsd(): string {
    return dollarsPrettified(this.tvlUsd)
  }

  get isActive(): boolean {
    return this.tags.includes("active")
  }

  get isBlueChip(): boolean {
    // Force MobX to observe the token's tag-derived fields to ensure reactivity
    // This creates a direct dependency chain: isBlueChip -> token.hasBlueChipTag -> isBlueChip
    this.token0.hasBlueChipTag
    this.token1.hasBlueChipTag

    const token0BlueChip = this.token0.hasBlueChipTag
    const token1BlueChip = this.token1.hasBlueChipTag

    return token0BlueChip && token1BlueChip
  }

  get isNew(): boolean {
    return this.tags.includes("new")
  }

  get gaugeReserves(): Array<bigint> {
    return [this.onChainGaugeReserve0, this.onChainGaugeReserve1]
  }

  get gaugeTotalSupplyPercentage(): number {
    return Number(this.formatUnits(this.onChainGaugeTotalSupply)) / Number(this.formatUnits(this.onChainTotalSupply))
  }

  get gaugeUserBalance(): bigint {
    return this.onChainUserGaugeBalance
  }

  get gaugeUserBalancePercentage(): number {
    return (
      Number(this.formatUnits(this.onChainUserGaugeBalance)) / Number(this.formatUnits(this.onChainGaugeTotalSupply))
    )
  }

  get gaugeUserBalanceToken0(): bigint {
    return this.onChainUserGaugeReserve0
  }

  get gaugeUserBalanceToken1(): bigint {
    return this.onChainUserGaugeReserve1
  }

  get gaugeUserBalanceValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.gaugeUserBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.gaugeUserBalanceToken1)) * this.token1.bestPrice
    )
  }

  get gaugeUserValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.gaugeUserBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.gaugeUserBalanceToken1)) * this.token1.bestPrice
    )
  }

  get gaugeValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.onChainGaugeReserve0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.onChainGaugeReserve1)) * this.token1.bestPrice
    )
  }

  get imageURL(): string {
    return ""
  }

  get reserve0(): bigint {
    if (this.onChainReserve0) {
      return this.onChainReserve0
    }
    if (this.apiReserve0) {
      return this.apiReserve0
    }
    return 0n
  }

  get reserve1(): bigint {
    if (this.onChainReserve1) {
      return this.onChainReserve1
    }
    if (this.apiReserve1) {
      return this.apiReserve1
    }
    return 0n
  }

  get reserves(): Array<bigint> {
    return [this.reserve0, this.reserve1]
  }

  get scanTokenURL(): string {
    return `https://basescan.org/token/${this.address}`
  }

  get symbol(): string {
    return `${this.token0.symbol}/${this.token1.symbol}`
  }

  get tokens(): Array<TokenStore> {
    return [this.token0, this.token1]
  }

  get totalSupply(): string {
    if (this.onChainTotalSupply > 0n) {
      return this.formatUnits(this.onChainTotalSupply)
    }
    if (this.apiTotalSupply > 0) {
      return this.formatUnits(this.apiTotalSupply)
    }
    return "0"
  }

  get tvlUsd(): number {
    if (false) {
      console.log("this.apiTvlUsd", this.apiTvlUsd)
      console.log("this.onChainReserve0", this.onChainReserve0)
      console.log("this.onChainReserve1", this.onChainReserve1)
      console.log("this.token0.bestPrice", this.token0.bestPrice)
      console.log("this.token1.bestPrice", this.token1.bestPrice)
    }
    if (this.onChainReserve0 > 0n && this.onChainReserve1 > 0n) {
      return (
        Number(this.token0.formatUnits(this.onChainReserve0)) * this.token0.bestPrice +
        Number(this.token1.formatUnits(this.onChainReserve1)) * this.token1.bestPrice
      )
    }
    // return this.apiTvlUsd
    return 0
  }

  get type(): ILiquidityPoolType {
    return this.isStable ? "stable" : "volatile"
  }

  get userBalance(): bigint {
    return this.onChainUserBalance
  }

  get userBalancePercentage(): number {
    return Number(this.formatUnits(this.userBalance)) / Number(this.totalSupply)
  }

  get userBalanceToken0(): bigint {
    return this.onChainUserBalanceToken0
  }

  get userBalanceToken1(): bigint {
    return this.onChainUserBalanceToken1
  }

  get userBalanceValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.userBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.userBalanceToken1)) * this.token1.bestPrice
    )
  }

  get walletValueUSD(): number {
    return (
      Number(this.token0.displayBalance) * this.token0.bestPrice +
      Number(this.token1.displayBalance) * this.token1.bestPrice
    )
  }

  /**
   * Pool specific unit formatting (uses internal decimals)
   * @param wei
   * @returns
   */
  formatUnits(wei: bigint): string {
    return formatUnits(wei, this.decimals)
  }

  formatUnitsAsNumber(wei: bigint): number {
    return Number(formatUnits(wei, this.decimals))
  }

  parseUnits(wei: string): bigint {
    return parseUnits(wei, this.decimals)
  }

  // Update static data (useful for refreshing from API)
  updateStaticData(data: Partial<MinimalPoolApiData>): void {
    if (data.apr !== undefined) this.apr = data.apr
    if (data.aprLastEpoch !== undefined) this.aprLastEpoch = data.aprLastEpoch
    if (data.fee !== undefined) this.fee = data.fee || 0
    if (data.reserve0 !== undefined) this.apiReserve0 = this.token0.parseUnits(data.reserve0) || 0n
    if (data.reserve1 !== undefined) this.apiReserve1 = this.token1.parseUnits(data.reserve1) || 0n
    if (data.tags !== undefined) this.tags = data.tags
    if (data.totalSupply !== undefined) this.apiTotalSupply = this.parseUnits(data.totalSupply) || 0n
    if (data.tvlUsd !== undefined) this.apiTvlUsd = data.tvlUsd
  }

  updateInfoPairValues(infoPair: DefaultInfoPairValues): void {
    // if (this.address.equalsString("0x23692f630b15c8931ade1321e09d9155e6706b06")) {
    //   // TRENCHES/USDC
    //   console.log("infoPair", infoPair)
    //   console.log("this", this)
    // }

    this.onChainTotalSupply = infoPair.totalSupply
    this.onChainReserve0 = infoPair.reserve0
    this.onChainReserve1 = infoPair.reserve1
    this.onChainGaugeTotalSupply = infoPair.gaugeTotalSupply
    this.onChainGaugeReserve0 = infoPair.gaugeReserve0
    this.onChainGaugeReserve1 = infoPair.gaugeReserve1
  }

  // 0xb217dA64836DDB800559ac3369f702ee1DB0E1e5
  updateScalensUserInfoPairValues(userInfoPair: ScalensUserInfoPairs): void {
    this.onChainUserBalance = userInfoPair.userPairBalance
    this.onChainUserBalanceToken0 = userInfoPair.userPairReserve0 // token decimals
    this.onChainUserBalanceToken1 = userInfoPair.userPairReserve1 // token decimals
    this.onChainUserClaimableFees0 = userInfoPair.userPairClaimableFees0 // token decimals
    this.onChainUserClaimableFees1 = userInfoPair.userPairClaimableFees1 // token decimals
    this.onChainUserGaugeBalance = userInfoPair.userGaugeBalance
    this.onChainUserGaugeGovernanceEarned = userInfoPair.userGaugeGovernanceEarned
    this.onChainUserGaugeReserve0 = userInfoPair.userGaugeReserve0
    this.onChainUserGaugeReserve1 = userInfoPair.userGaugeReserve1

    // debug
    // if (userInfoPair.pairAddress.equalsString("0x3994018571cb07e48ccc98d27857062bc8969dbe")) {
    //   // TRENCHES/USDC
    //   console.log("userInfoPair", userInfoPair)
    //   console.log("this", this)
    // }
    // if (userInfoPair.pairAddress.equalsString("0x23692f630b15c8931ade1321e09d9155e6706b06")) {
    //   // TRENCHES/USDC
    //   console.log("userInfoPair", userInfoPair)
    //   console.log("this", this)
    // }
    // if (userInfoPair.pairAddress.equalsString("0xb217da64836ddb800559ac3369f702ee1db0e1e5")) {
    //   // TRENCHES/SCALE
    //   console.log("userInfoPair", userInfoPair)
    //   console.log("TRENCHES/SCALE", this)
    // }
    //
  }

  updateUserManageValues(userManageValues: ScalensUserManageMinimalPoolInfo): void {
    this.onChainGaugeTotalSupply = userManageValues.gaugeTotalSupply
    this.onChainReserve0 = userManageValues.poolReserve0
    this.onChainReserve1 = userManageValues.poolReserve1
    this.onChainTotalSupply = userManageValues.poolTotalSupply

    this.onChainUserBalance = userManageValues.userPoolBalance
    this.onChainUserBalanceToken0 = userManageValues.userPoolReserve0
    this.onChainUserBalanceToken1 = userManageValues.userPoolReserve1

    this.onChainUserGaugeBalance = userManageValues.userGaugeBalance
    this.onChainUserGaugeReserve0 = userManageValues.userGaugeReserve0
    this.onChainUserGaugeReserve1 = userManageValues.userGaugeReserve1

    this.token0.updateBalance(userManageValues.userToken0Balance)
    this.token1.updateBalance(userManageValues.userToken1Balance)

    // allowances
    this.onChainUserGaugeAllowance = userManageValues.userPoolGaugeAllowance
    this.onChainUserRouterAllowance = userManageValues.userPoolRouterAllowance
    this.onChainUserToken0RouterAllowance = userManageValues.userToken0RouterAllowance
    this.onChainUserToken1RouterAllowance = userManageValues.userToken1RouterAllowance
  }

  connectToGauge(gauge: IGauge): void {
    runInAction(() => {
      this.gauge = gauge
      // Also connect the gauge to this pool as a revenue source
      // Pass 'this' directly to maintain MobX reactivity - the gauge will observe changes to this store
      gauge.connectToRevenueSource(this)
    })
  }
}
